import {
  Box,
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  Legend,
  TooltipContentProps,
  XAxis,
  YAxis,
} from 'recharts';

import { createBarValueLabel } from './recharts/BarValueLabel';
import CursorTooltip from './recharts/CursorTooltip';
import { CustomTooltip } from './recharts/CustomTooltip';
import { useExportLegendConfig } from './recharts/hooks/useExportLegendConfig';
import { useLegendInteraction } from './recharts/hooks/useLegendInteraction';
import { LegendContent } from './recharts/LegendContent';
import ReChartContainer, {
  ChartTitles,
  useAxisSharedProps,
} from './recharts/ReChartContainer';
import type { BarChartProps, SeriesSelectionConfig } from './recharts/types';
import { getPalette } from './recharts/utils/colors';
import {
  computeChartMargins,
  getChartTypography,
} from './recharts/utils/layout';

export type Props = BarChartProps;

function SeriesSelectionControl({
  config,
  selectedGroupIds,
  onChange,
}: {
  config: SeriesSelectionConfig;
  selectedGroupIds: string[];
  onChange: (groupIds: string[]) => void;
}) {
  const maxVisibleGroups = config.maxVisibleGroups ?? 2;
  const groupLabelMap = useMemo(
    () => new Map(config.groups.map((group) => [group.id, group.label])),
    [config.groups]
  );
  const selectedLabels = selectedGroupIds
    .map((groupId) => groupLabelMap.get(groupId))
    .filter((label): label is string => Boolean(label));
  const isAtLimit = selectedGroupIds.length >= maxVisibleGroups;

  return (
    <FormControl
      size='small'
      sx={{
        minWidth: { xs: '100%', sm: 320 },
        maxWidth: { xs: '100%', sm: 420 },
      }}
    >
      <InputLabel>{config.label ?? 'Select items'}</InputLabel>
      <Select
        multiple
        value={selectedGroupIds}
        label={config.label ?? 'Select items'}
        renderValue={() => {
          if (selectedLabels.length === 0) {
            return config.label ?? 'Select items';
          }

          if (selectedLabels.length <= 2) {
            return selectedLabels.join(', ');
          }

          return `${selectedLabels.slice(0, 2).join(', ')} +${
            selectedLabels.length - 2
          }`;
        }}
        onChange={(event) => {
          const nextValue =
            typeof event.target.value === 'string'
              ? event.target.value.split(',')
              : (event.target.value as string[]);

          if (nextValue.length === 0) {
            return;
          }

          onChange(nextValue.slice(0, maxVisibleGroups));
        }}
      >
        {config.groups.map((group) => {
          const checked = selectedGroupIds.includes(group.id);
          const shouldDisable = !checked && isAtLimit;

          return (
            <MenuItem
              key={group.id}
              value={group.id}
              disabled={shouldDisable}
              dense
              sx={{
                py: 0.25,
                minHeight: 32,
                '& .MuiCheckbox-root': {
                  p: 0.5,
                  mr: 0.5,
                },
              }}
            >
              <Checkbox checked={checked} size='small' />
              <ListItemText
                primary={group.label}
                primaryTypographyProps={{ variant: 'body2' }}
                sx={{ my: 0 }}
              />
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
}

export default function ReBarChart({
  series,
  categories,
  title,
  subtitle,
  yTitle,
  xTitle,
  syncId,
  action,
  bucketOptions,
  showLegend = false,
  height = '100%',
  fileName,
  hideExport = false,

  stackGroups = false,
  unit,
  colors,
  barSize,
  barOpacity = 0.8,
  stackOffset,
  yFormatter,
  xFormatter,
  tooltipXFormatter,
  rotateXTicks = false,
  isLoading = false,
  timeframeOptions,
  showTooltipTotal = false,
  showNegativeSign = false,
  tooltipTotals,
  filterNullTooltipItems = false,
  seriesSelection,
  valueLabels,
  xSubLabels,
}: Props) {
  const theme = useTheme();

  const palette = getPalette(colors);
  const { fontFamily, titleFontSize, subtitleFontSize, axisFontSize } =
    getChartTypography(theme);
  const { legendTop, margin } = computeChartMargins({
    titleFontSize: title ? titleFontSize : 0,
    subtitleFontSize: subtitle ? subtitleFontSize : 0,
    showLegend,
    yTitle,
    xTitle,
    rotateXTicks,
  });

  const legend = useLegendInteraction();
  const selectionLimit = Math.max(seriesSelection?.maxVisibleGroups ?? 4, 1);
  const shouldShowSeriesSelector =
    seriesSelection != null && seriesSelection.groups.length > selectionLimit;
  const selectionKey = useMemo(
    () =>
      shouldShowSeriesSelector
        ? `${selectionLimit}:${seriesSelection!.groups
            .map((group) => group.id)
            .join('|')}`
        : '',
    [selectionLimit, seriesSelection, shouldShowSeriesSelector]
  );
  const defaultSelectedGroupIds = useMemo(
    () =>
      shouldShowSeriesSelector
        ? seriesSelection!.groups
            .slice(0, selectionLimit)
            .map((group) => group.id)
        : (seriesSelection?.groups.map((group) => group.id) ?? []),
    [selectionLimit, selectionKey, seriesSelection, shouldShowSeriesSelector]
  );
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>(
    defaultSelectedGroupIds
  );

  useEffect(() => {
    setSelectedGroupIds(defaultSelectedGroupIds);
  }, [defaultSelectedGroupIds, selectionKey]);

  const filteredSeries = useMemo(() => {
    if (!seriesSelection || !shouldShowSeriesSelector) {
      return series;
    }

    const selectedGroupIdSet = new Set(selectedGroupIds);
    const selectedSeriesNames = new Set(
      seriesSelection.groups
        .filter((group) => selectedGroupIdSet.has(group.id))
        .flatMap((group) => group.seriesNames)
    );

    return series.filter((item) => selectedSeriesNames.has(item.name));
  }, [series, seriesSelection, selectedGroupIds, shouldShowSeriesSelector]);

  const getLegendExportConfig = useExportLegendConfig({
    series: filteredSeries,
    colors: palette,
    isHidden: legend.isSeriesHidden,
    y: legendTop,
  });

  const headerContent =
    shouldShowSeriesSelector && seriesSelection ? (
      <Box display='flex' justifyContent='flex-end'>
        <SeriesSelectionControl
          config={seriesSelection}
          selectedGroupIds={selectedGroupIds}
          onChange={setSelectedGroupIds}
        />
      </Box>
    ) : undefined;

  const data = useMemo(() => {
    const isTimeAxis = !!bucketOptions;
    const isNumericCategory =
      !isTimeAxis &&
      categories.every((c) => {
        const n = Number(c);
        return Number.isFinite(n);
      });

    return categories.map((ts, i) => {
      const tsMs = dayjs(ts).valueOf();
      const point: Record<string, any> = { timestamp: ts, ts: tsMs };
      if (isNumericCategory) {
        const n = Number(ts);
        point.timestampNumeric = Number.isFinite(n) ? n : undefined;
      }
      filteredSeries.forEach((s) => {
        point[s.name] = s.data[i] ?? null;
      });
      return point;
    });
  }, [filteredSeries, categories, bucketOptions]);

  const isTimeAxisGlobal = !!bucketOptions;
  const areCategoriesNumeric = useMemo(
    () => categories.every((c) => Number.isFinite(Number(c))),
    [categories]
  );

  const xAxisDataKey = isTimeAxisGlobal
    ? 'ts'
    : areCategoriesNumeric
      ? 'timestampNumeric'
      : 'timestamp';

  const subLabelByTick = useMemo(
    () =>
      xSubLabels
        ? new Map(
            data.map((point, i) => [
              String(point[xAxisDataKey]),
              xSubLabels[i] ?? null,
            ])
          )
        : null,
    [data, xAxisDataKey, xSubLabels]
  );
  const xTickSubLabel = useCallback(
    (value: string | number) => subLabelByTick?.get(String(value)),
    [subLabelByTick]
  );

  const {
    xAxisProps,
    yAxisProps,
    defaultTooltipXFormatter,
    defaultYFormatter,
  } = useAxisSharedProps({
    data: data as Array<{
      timestamp: string | number;
      ts: number;
      [key: string]: any;
    }>,
    xTitle,
    yTitle,
    bucketOptions,
    xFormatter,
    yFormatter,
    tooltipXFormatter,
    rotateXTicks,
    xAxisType: 'category',
    xAxisInterval: subLabelByTick ? 0 : undefined,
    timeframeOptions,
    xTickSubLabel: subLabelByTick ? xTickSubLabel : undefined,
  });

  const valueLabelOptions =
    valueLabels === true ? {} : valueLabels ? valueLabels : null;

  return (
    <ReChartContainer
      hideExport={hideExport}
      height={height}
      title={title}
      fileName={fileName}
      series={filteredSeries}
      categories={categories}
      getLegendExportConfig={getLegendExportConfig}
      isLoading={isLoading}
      headerContent={headerContent}
      action={action}
    >
      <BarChart
        data={data}
        margin={margin}
        barGap={2}
        barCategoryGap={'15%'}
        stackOffset={stackOffset}
        syncId={syncId}
        syncMethod='value'
      >
        <ChartTitles title={title} subtitle={subtitle} />
        <CartesianGrid vertical={false} stroke={theme.palette.divider} />
        <XAxis {...xAxisProps} dataKey={xAxisDataKey} />
        <YAxis {...yAxisProps} />
        <CursorTooltip
          syncId={syncId}
          filterNull={filterNullTooltipItems}
          wrapperStyle={{ zIndex: 1 }}
          content={(props: TooltipContentProps<number, string>) => (
            <CustomTooltip
              {...props}
              yFormatter={defaultYFormatter}
              xFormatter={defaultTooltipXFormatter}
              unit={unit}
              showTotal={showTooltipTotal}
              showNegativeSign={showNegativeSign}
              totals={tooltipTotals}
            />
          )}
        />
        {showLegend && (
          <Legend
            verticalAlign='top'
            align='center'
            iconType='circle'
            wrapperStyle={{ fontFamily, top: legendTop }}
            content={(props) => (
              <LegendContent {...props} legend={legend} iconType='circle' />
            )}
          />
        )}
        {filteredSeries.map((s, idx) => {
          const isHidden = legend.isSeriesHidden(s.name);
          const isDimmed = !isHidden && legend.isSeriesDimmed(s.name);
          const baseColor = palette[idx % palette.length];
          const finalBarOpacity = barOpacity * (isDimmed ? 0.5 : 1);

          // Determine stackId: when stackGroups is true and series has a group, use group as stackId
          const stackId = stackGroups && s.group ? s.group : undefined;

          return (
            <Bar
              key={s.name}
              dataKey={s.name}
              stackId={stackId}
              fill={baseColor}
              fillOpacity={finalBarOpacity}
              stroke={baseColor}
              strokeOpacity={isDimmed ? 0.6 : 1}
              barSize={barSize}
              hide={isHidden}
            >
              {valueLabelOptions && (
                <LabelList
                  dataKey={s.name}
                  content={createBarValueLabel({
                    formatter: valueLabelOptions.formatter ?? defaultYFormatter,
                    vertical: valueLabelOptions.vertical ?? false,
                    fontFamily,
                    fontSize: axisFontSize - 1,
                    insideFill: theme.palette.getContrastText(baseColor),
                    outsideFill: theme.palette.text.secondary,
                  })}
                />
              )}
            </Bar>
          );
        })}
      </BarChart>
    </ReChartContainer>
  );
}
